import { LitElement, PropertyValues, css, html } from 'lit'
import { property, state } from 'lit/decorators.js'

/**
 * Time Input Component with Overnight Hours Support
 * 
 * Features:
 * - Supports 24:00 format for midnight next day
 * - Visual highlighting for overnight times (24:xx)
 * - Cross-platform input handling (Android vs standard)
 * - Real-time validation and formatting
 * 
 * Usage:
 * <time-input value="23:30" name="closing-time"></time-input>
 * <time-input value="24:00" name="overnight-end"></time-input>
 */
export class TimeInput extends LitElement {
  @property({ type: String, reflect: true }) value = '00:00'
  @property({ type: String }) name = 'time-input'
  
  @state() private hours = '00'
  @state() private minutes = '00'

  private syncPartsFromValue() {
    const [hours = '00', minutes = '00'] = (this.value || '00:00').split(':')
    this.hours = hours.padStart(2, '0')
    this.minutes = minutes.padStart(2, '0')
  }

  connectedCallback() {
    super.connectedCallback()
    this.syncPartsFromValue()
  }

  protected willUpdate(changedProperties: PropertyValues<this>) {
    if (changedProperties.has('value')) {
      this.syncPartsFromValue()
    }
  }

  private handleInput(event: Event) {
    const input = event.target as HTMLInputElement
    let numValue = parseInt(input.value, 10) || 0

    const nextInput = input.nextElementSibling as HTMLInputElement | null
    
    if (input.name === `${this.name}-hours`) {
      numValue = Math.min(Math.max(numValue, 0), 24)
      this.hours = numValue.toString().padStart(2, '0')
      if (numValue === 24) {
        this.minutes = '00'
      }

      if (numValue > 2) {
        input.value = numValue.toString().padStart(2, '0')
        nextInput?.focus()
      } 
       
    } else if (input.name === `${this.name}-minutes`) {
      numValue = Math.min(Math.max(numValue, 0), 59)
      if (this.hours === '24') {
        numValue = 0
      }
      this.minutes = numValue.toString().padStart(2, '0')
    } else if (input.name === `${this.name}`) {
      // Handle direct time input (HH:MM format) - HTML5 time inputs always provide 24-hour values
      const [hours, minutes] = input.value.split(':')
      const hourNum = parseInt(hours, 10) || 0
      const minuteNum = parseInt(minutes, 10) || 0
      const nextHour = Math.min(Math.max(hourNum, 0), 24)
      
      this.hours = nextHour.toString().padStart(2, '0')
      this.minutes = (nextHour === 24 ? 0 : Math.min(Math.max(minuteNum, 0), 59)).toString().padStart(2, '0')
    }

    this.value = `${this.hours}:${this.minutes}`
    this.dispatchEvent(new CustomEvent('timeinput', { detail: this.value }))
    this.requestUpdate()
  }

  private handleBlur(event: FocusEvent) {
    const input = event.target as HTMLInputElement
    input.value ||= input.name.includes('begin') ? '00' : '00'
  }

  private handleFocus(event: FocusEvent) {
    const input = event.target as HTMLInputElement
    input.value = ''
  }

  private isAndroid(): boolean {
    return /Android/i.test(navigator.userAgent) && 'ontouchstart' in window
  }

  private renderAndroidInput() {
    return html`
      <input type="text" inputmode="numeric" pattern="[0-9]*" name="${this.name}-hours"
             .value=${this.hours} placeholder="--"
             autocomplete="off" @input=${this.handleInput} @blur=${this.handleBlur} @focus=${this.handleFocus} /> :
      <input type="text" inputmode="numeric" pattern="[0-9]*" name="${this.name}-minutes"
             .value=${this.minutes} placeholder="--"
             autocomplete="off" @input=${this.handleInput} @blur=${this.handleBlur} @focus=${this.handleFocus} />
    `
  }

  private renderStandardInput() {
    return html`<input type="time" name="${this.name}" .value=${this.value} @input=${this.handleInput} />`
  }

  render() {
    return this.isAndroid() ? this.renderAndroidInput() : this.renderStandardInput()
  }

  static styles = css`
    :host {
      display: inline-block;
    }

    :host[value="00:00"] {
      opacity: 0.5;
    }

    /* Highlight 24:xx times to indicate overnight hours */
    :host([value^="24:"]) {
      background-color: rgba(96, 155, 226, 0.1);
      border-radius: 4px;
      padding: 2px;
    }

    input {
      border: none;
      border-bottom: 1px solid grey;
      background-color: transparent;
      text-align: center;
      min-height: 20px;
      font-size: 18px;
      outline: none;
      border-radius: 0;
    }

    input[type="text"] {
      width: 2ch;
    }

    input:focus, input:active {
      background-color: #5f8ec79b;
    }
  `
}

if (!customElements.get('time-input')) {
  customElements.define('time-input', TimeInput)
}

declare global {
  interface HTMLElementTagNameMap {
    'time-input': TimeInput
  }
}
